/**
 * Product Quote Button Handler
 * Handles showing quote modal for regular products
 */

/**
 * Get product properties from form
 */
function getProductProperties(form) {
  const properties = {};

  if (!form) return properties;

  // Get selected variant options
  const variantInputs = form.querySelectorAll('input[name="options[]"], select[name="options[]"]');
  variantInputs.forEach(input => {
    if (input.value) {
      const label = input.getAttribute('aria-label') || input.name;
      properties[label] = input.value;
    }
  });

  // Get any custom properties
  const customInputs = form.querySelectorAll('input[name^="properties"], textarea[name^="properties"]');
  customInputs.forEach(input => {
    if (input.value) {
      const propertyName = input.name.match(/properties\[(.*?)\]/)?.[1];
      if (propertyName) {
        properties[propertyName] = input.value;
      }
    }
  });

  return properties;
}

/**
 * Add item directly to quote cart without showing modal
 */
function addToQuoteCartDirect(item) {
  const storageKey = 'sms_quote_cart';
  let cart;

  try {
    const cartData = localStorage.getItem(storageKey);
    cart = cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
  } catch (error) {
    console.error('Error reading quote cart:', error);
    cart = { items: [], createdAt: new Date().toISOString() };
  }

  // Check if an identical item already exists
  const existingItem = cart.items.find(cartItem => {
    // Check if variant IDs match
    if (cartItem.variantId !== item.variantId) {
      return false;
    }

    // Check if properties match
    const props1 = cartItem.properties || {};
    const props2 = item.properties || {};

    const keys1 = Object.keys(props1).sort();
    const keys2 = Object.keys(props2).sort();

    // Different number of properties
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check each property value
    for (let key of keys1) {
      if (props1[key] !== props2[key]) {
        return false;
      }
    }

    return true;
  });

  if (existingItem) {
    // Item exists, increase quantity
    existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
    console.log('Increased quantity for existing item:', existingItem);
  } else {
    // New item, add to cart
    const newItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      ...item,
      addedAt: new Date().toISOString(),
    };

    cart.items.push(newItem);
    console.log('Item saved to quote cart:', newItem);
  }

  try {
    localStorage.setItem(storageKey, JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving to quote cart:', error);
    throw new Error('Unable to save to quote cart');
  }

  // Dispatch event for icon update
  document.dispatchEvent(new CustomEvent('quote-cart-updated', {
    detail: { cart, action: 'add', item: existingItem || item }
  }));
}

/**
 * Handle add to quote button click
 */
async function handleAddToQuote(event) {
  const button = event.target.closest('[name="add-to-quote"]');
  if (!button) return;

  event.preventDefault();

  const spinner = button.querySelector('.loading-overlay__spinner');

  // Start loading state
  button.setAttribute('aria-busy', 'true');
  button.classList.add('button--loading');
  button.disabled = true;
  if (spinner) {
    spinner.classList.remove('hidden');
  }

  try {
    // Get product data from button attributes
    const variantId = `gid://shopify/ProductVariant/${button.dataset.productId}`;
    const title = button.dataset.productTitle;
    const price = parseInt(button.dataset.productPrice);
    const image = button.dataset.productImage;

    // Get quantity from the form
    // For product cards, use the form ID to find the associated quantity input
    let quantityInput;
    const productFormId = button.dataset.productFormId;

    if (productFormId) {
      // Product card: find input with form attribute matching the form ID
      quantityInput = document.querySelector(`input[name="quantity"][form="${productFormId}"]`);
    } else {
      // Product detail page: find within the closest product-form
      const form = button.closest('product-form');
      quantityInput = form?.querySelector('input[name="quantity"]');
    }

    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

    // Get variant options/properties if any
    // Find the form element
    const form = productFormId ? document.getElementById(productFormId) : button.closest('product-form');
    const properties = getProductProperties(form);

    // Prepare cart item
    const cartItem = {
      variantId: variantId,
      quantity: quantity,
      title: title,
      price: price,
      image: image,
      properties: properties
    };

    // Reset button state before showing modal
    button.removeAttribute('aria-busy');
    button.classList.remove('button--loading');
    button.disabled = false;
    if (spinner) {
      spinner.classList.add('hidden');
    }

    // Get the quote threshold (in cents)
    const thresholdCents = window.SMS_QUOTE_THRESHOLD || 50000; // Default to $500 if not set

    // Check if item price is below threshold
    const totalPrice = price * quantity;

    if (totalPrice < thresholdCents) {
      // Price is below threshold - add directly to quote cart without modal
      console.log(`Item price ($${totalPrice / 100}) is below threshold ($${thresholdCents / 100}). Adding directly to quote cart.`);

      // Add to quote cart directly
      addToQuoteCartDirect(cartItem);

      // Show success dialog
      if (window.QuoteSuccessDialog && window.SMS_QUOTE_SETTINGS) {
        window.QuoteSuccessDialog.show({
          title: window.SMS_QUOTE_SETTINGS.addedTitle,
          message: window.SMS_QUOTE_SETTINGS.addedMessage,
          buttonText: 'View Quote Cart',
          buttonUrl: '/pages/quote-cart'
        });
      }

    } else {
      // Price is at or above threshold - show the quote modal
      console.log(`Item price ($${totalPrice / 100}) meets threshold ($${thresholdCents / 100}). Showing quote form.`);

      if (window.SMS_QuoteModal && window.SMS_QuoteModal.showForSingleItem) {
        window.SMS_QuoteModal.showForSingleItem(cartItem);
      } else {
        throw new Error('Quote modal system not loaded. Please refresh the page and try again.');
      }
    }

  } catch (error) {
    console.error('Error adding to quote:', error);
    alert(`Failed to show quote form: ${error.message}`);

    // Reset button state on error
    button.removeAttribute('aria-busy');
    button.classList.remove('button--loading');
    button.disabled = false;
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }
}

// Initialize event listeners only once
if (typeof document !== 'undefined' && !window.SMS_QuoteButtonInitialized) {
  window.SMS_QuoteButtonInitialized = true;
  // Use event delegation on document for all add-to-quote buttons
  document.addEventListener('click', handleAddToQuote);
}
